import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Calendar, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight,
  ChevronUp, ChevronDown, ChevronsUpDown, Filter, RefreshCw, Search, X, Printer,
  ChevronRight as DrillIcon, List, FolderTree, Tag, Layers,
} from 'lucide-react';
import { vouchersApi } from '../services/api';
import { useToast } from '../components/Toast/Toast';
import useChangePeriodKey from '../hooks/useChangePeriodKey';

const fmt = (n: any) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQty = (n: any) =>
  Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 3 });

const toInputDate = (d: Date) => d.toISOString().split('T')[0];
const displayDate = (s?: string | null) => {
  if (!s) return '—';
  const d = new Date(s);
  return `${String(d.getDate()).padStart(2, '0')}-${String(d.getMonth() + 1).padStart(2, '0')}-${d.getFullYear()}`;
};

type Row = {
  item_id: number;
  item_name: string;
  group_name: string | null;
  gst: number | null;
  opening_qty: number;
  opening_value: number;
  inward_qty: number;
  inward_value: number;
  outward_qty: number;
  outward_value: number;
  closing_qty: number;
  closing_value: number;
};
type SortKey =
  | 'item_name' | 'group_name'
  | 'opening_qty' | 'inward_qty' | 'outward_qty' | 'closing_qty' | 'closing_value';
type SortDir = 'asc' | 'desc';
const PAGE_SIZES = [10, 25, 50, 100];
const STORAGE_KEY = 'stock-summary-filters';

type ViewMode = 'items' | 'groups' | 'categories';
type BreadcrumbItem = { id: number; name: string };
type GroupRow = {
  row_type: 'group' | 'item';
  id: number;
  name: string;
  item_count?: number | null;
  opening_qty: number; opening_value: number;
  inward_qty: number; inward_value: number;
  outward_qty: number; outward_value: number;
  closing_qty: number; closing_value: number;
};
const GROUP_PAGE_SIZE = 50;
type BatchRow = {
  batch_name: string;
  opening_qty: number; opening_value: number;
  inward_qty: number; inward_value: number;
  outward_qty: number; outward_value: number;
  closing_qty: number; closing_value: number;
};
type TxnRow = {
  sno: number;
  vch_id: number;
  vch_no: string | null;
  vch_date: string | null;
  vch_type_name: string | null;
  particulars: string;
  opening_qty: number; opening_value: number;
  inward_qty: number; inward_value: number;
  outward_qty: number; outward_value: number;
  closing_qty: number; closing_value: number;
};
// Item-scoped drill-down within Stock Summary: item → batches → one batch's
// voucher register. Layered on top of whichever list (flat items, or a
// group/category leaf) is currently showing; clearing it returns to that
// same list without re-fetching it.
type ItemDrill = { itemId: number; itemName: string; batchName?: string };

function fyBounds(d: Date) {
  const y = d.getMonth() >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  return { from: new Date(y, 3, 1), to: new Date(y + 1, 2, 31) };
}
function defaultFilters() {
  const today = new Date();
  const { from } = fyBounds(today);
  return { dateFrom: toInputDate(from), dateTo: toInputDate(today), search: '' };
}

export default function StockSummary() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { showError } = useToast();
  const initial = (() => {
    // A date range passed in the URL (e.g. drilling "Stock-in-Hand" from Group
    // Summary) wins, so the stock period matches the group summary the user came from.
    const urlFrom = searchParams.get('date_from');
    const urlTo = searchParams.get('date_to');
    let base = defaultFilters();
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) base = { ...base, ...JSON.parse(saved) };
    } catch { /* ignore */ }
    if (urlFrom) base.dateFrom = urlFrom;
    if (urlTo) base.dateTo = urlTo;
    return base;
  })();

  const [dateFrom, setDateFrom] = useState<string>(initial.dateFrom);
  const [dateTo, setDateTo]     = useState<string>(initial.dateTo);
  const [search, setSearch]     = useState<string>(initial.search);
  const [debouncedSearch, setDebouncedSearch] = useState<string>(initial.search);
  // Hide rows that are pure zero (no opening, no movement, no closing) so
  // the report stays focused on items that actually moved.
  const [hideZero, setHideZero] = useState<boolean>(false);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<Row[]>([]);
  const [totals, setTotals] = useState({ opening_value: 0, inward_value: 0, outward_value: 0, closing_value: 0 });

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({ dateFrom, dateTo, search })); } catch { /* ignore */ }
  }, [dateFrom, dateTo, search]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(t);
  }, [search]);

  const [sortKey, setSortKey] = useState<SortKey>('item_name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  const [page, setPage] = useState(1);
  // Rows/page auto-fits the viewport: how many ~37px rows fit under the header,
  // filters and pagination bar. Recomputed on resize while in Auto mode; a manual
  // pick from the dropdown pins the size until the user switches back to Auto.
  const computeFitRows = () => Math.max(8, Math.floor((window.innerHeight - 250) / 37));
  const [autoFit, setAutoFit] = useState(true);
  const [pageSize, setPageSize] = useState<number>(() => computeFitRows());
  useEffect(() => {
    if (!autoFit) return;
    const apply = () => setPageSize(computeFitRows());
    apply();
    window.addEventListener('resize', apply);
    return () => window.removeEventListener('resize', apply);
  }, [autoFit]);
  const [showControls, setShowControls] = useState(false);
  // Alt+F2 — Tally's Change Period: open the date filter, cursor in From.
  useChangePeriodKey(() => setShowControls(true));
  const [printing, setPrinting] = useState(false);
  useEffect(() => {
    const onAfter = () => setPrinting(false);
    window.addEventListener('afterprint', onAfter);
    return () => window.removeEventListener('afterprint', onAfter);
  }, []);
  const handlePrint = () => {
    if ((viewMode === 'items' ? rows.length : groupRows.length) === 0) return;
    setPrinting(true);
    setTimeout(() => window.print(), 50);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await vouchersApi.getStockSummary({
        date_from: dateFrom || undefined,
        date_to: dateTo || undefined,
        search: debouncedSearch || undefined,
      });
      if (res.success) {
        setRows(res.data.rows || []);
        setTotals({
          opening_value: res.data.totals?.opening_value || 0,
          inward_value:  res.data.totals?.inward_value  || 0,
          outward_value: res.data.totals?.outward_value || 0,
          closing_value: res.data.totals?.closing_value || 0,
        });
      }
    } catch { showError('Error', 'Failed to load Stock Summary'); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo, debouncedSearch, showError]);

  // ── Group drill-down mode (Tally-style: Group > Group > … > Item) ──
  const [viewMode, setViewMode] = useState<ViewMode>('items');
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbItem[]>([]);
  const [groupRows, setGroupRows] = useState<GroupRow[]>([]);
  const [groupTotals, setGroupTotals] = useState({ opening_value: 0, inward_value: 0, outward_value: 0, closing_value: 0 });
  const [groupLoading, setGroupLoading] = useState(false);
  const [groupPage, setGroupPage] = useState(1);
  const currentGroupId = breadcrumb.length > 0 ? breadcrumb[breadcrumb.length - 1].id : null;

  const loadGroups = useCallback(async () => {
    setGroupLoading(true);
    try {
      const byCategory = viewMode === 'categories';
      const summaryApi = byCategory ? vouchersApi.getStockCategorySummary : vouchersApi.getStockGroupSummary;
      const itemsApiFn  = byCategory ? vouchersApi.getStockCategoryItems  : vouchersApi.getStockGroupItems;

      if (currentGroupId === null) {
        const res = await summaryApi({
          date_from: dateFrom || undefined, date_to: dateTo || undefined, search: debouncedSearch || undefined,
        });
        if (res.success) {
          setGroupRows((res.data.rows || []).map((g: any) => ({
            row_type: 'group', id: g.group_id, name: g.group_name, item_count: g.item_count,
            opening_qty: g.opening_qty, opening_value: g.opening_value,
            inward_qty: g.inward_qty, inward_value: g.inward_value,
            outward_qty: g.outward_qty, outward_value: g.outward_value,
            closing_qty: g.closing_qty, closing_value: g.closing_value,
          })));
          setGroupTotals({
            opening_value: res.data.totals?.opening_value || 0,
            inward_value:  res.data.totals?.inward_value  || 0,
            outward_value: res.data.totals?.outward_value || 0,
            closing_value: res.data.totals?.closing_value || 0,
          });
        }
      } else {
        const res = await itemsApiFn(currentGroupId, {
          date_from: dateFrom || undefined, date_to: dateTo || undefined, search: debouncedSearch || undefined,
        });
        if (res.success) {
          setGroupRows((res.data.rows || []).map((r: any) => ({
            row_type: r.row_type === 'subgroup' ? 'group' : 'item',
            id: r.row_type === 'subgroup' ? r.group_id : r.item_id, name: r.item_name, item_count: r.item_count,
            opening_qty: r.opening_qty, opening_value: r.opening_value,
            inward_qty: r.inward_qty, inward_value: r.inward_value,
            outward_qty: r.outward_qty, outward_value: r.outward_value,
            closing_qty: r.closing_qty, closing_value: r.closing_value,
          })));
          setGroupTotals({
            opening_value: res.data.totals?.opening_value || 0,
            inward_value:  res.data.totals?.inward_value  || 0,
            outward_value: res.data.totals?.outward_value || 0,
            closing_value: res.data.totals?.closing_value || 0,
          });
        }
      }
    } catch { showError('Error', 'Failed to load Stock Summary groups'); }
    finally { setGroupLoading(false); }
  }, [viewMode, currentGroupId, dateFrom, dateTo, debouncedSearch, showError]);

  useEffect(() => { if (viewMode === 'items') load(); }, [viewMode, load]);
  useEffect(() => { if (viewMode === 'groups' || viewMode === 'categories') loadGroups(); }, [viewMode, loadGroups]);
  useEffect(() => { setPage(1); }, [debouncedSearch, dateFrom, dateTo, pageSize, sortKey, sortDir, hideZero]);
  useEffect(() => { setGroupPage(1); }, [currentGroupId, debouncedSearch, dateFrom, dateTo, hideZero]);

  const drillDown = (row: GroupRow) => {
    if (row.row_type === 'group') { setBreadcrumb(prev => [...prev, { id: row.id, name: row.name }]); setSearch(''); }
    else navigate(`/reports/stock-item?item_id=${row.id}`);
  };
  const openItemLedger = (itemId: number) => navigate(`/reports/stock-item?item_id=${itemId}`);
  const goToBreadcrumb = (idx: number) => { setBreadcrumb(prev => idx < 0 ? [] : prev.slice(0, idx + 1)); setSearch(''); };

  // ── Item drill-down: item → batches → one batch's voucher register ──
  // Separate, item-scoped action alongside the item transaction ledger
  // (Stock Summary only). Layered on top of whatever list (flat items, or
  // a group/category leaf) is currently showing.
  const [itemDrill, setItemDrill] = useState<ItemDrill | null>(null);
  const [batchLoading, setBatchLoading] = useState(false);
  const [batchRows, setBatchRows] = useState<BatchRow[]>([]);
  const [batchTotals, setBatchTotals] = useState({ opening_value: 0, inward_value: 0, outward_value: 0, closing_value: 0 });
  const [txnLoading, setTxnLoading] = useState(false);
  const [txnRows, setTxnRows] = useState<TxnRow[]>([]);
  const [txnOpening, setTxnOpening] = useState({ qty: 0, value: 0 });
  const [txnClosing, setTxnClosing] = useState({ qty: 0, value: 0 });

  const loadBatchSummary = useCallback(async (itemId: number) => {
    setBatchLoading(true);
    setBatchRows([]);
    try {
      const res = await vouchersApi.getItemBatchSummary(itemId, {
        date_from: dateFrom || undefined, date_to: dateTo || undefined,
      });
      if (res.success) {
        setBatchRows(res.data.rows || []);
        setBatchTotals({
          opening_value: res.data.totals?.opening_value || 0,
          inward_value:  res.data.totals?.inward_value  || 0,
          outward_value: res.data.totals?.outward_value || 0,
          closing_value: res.data.totals?.closing_value || 0,
        });
      }
    } catch { showError('Error', 'Failed to load batch-wise breakdown'); }
    finally { setBatchLoading(false); }
  }, [dateFrom, dateTo, showError]);

  const loadBatchTransactions = useCallback(async (itemId: number, batchName: string) => {
    setTxnLoading(true);
    setTxnRows([]);
    try {
      const res = await vouchersApi.getItemBatchVoucherRegister(itemId, {
        batch_name: batchName, date_from: dateFrom || undefined, date_to: dateTo || undefined,
      });
      if (res.success) {
        setTxnRows(res.data.rows || []);
        setTxnOpening(res.data.opening || { qty: 0, value: 0 });
        setTxnClosing(res.data.closing || { qty: 0, value: 0 });
      }
    } catch { showError('Error', 'Failed to load batch transactions'); }
    finally { setTxnLoading(false); }
  }, [dateFrom, dateTo, showError]);

  // Level 1: open the batch list for an item.
  const openItemBatches = (itemId: number, itemName: string) => {
    setItemDrill({ itemId, itemName });
    loadBatchSummary(itemId);
  };
  // Level 2: drill into one batch's voucher register.
  const openBatchTransactions = (batchName: string) => {
    if (!itemDrill) return;
    setItemDrill({ ...itemDrill, batchName });
    loadBatchTransactions(itemDrill.itemId, batchName);
  };
  // Back navigation within the item drill: transactions → batch list → exit.
  const backFromItemDrill = () => {
    setItemDrill(prev => (prev?.batchName ? { itemId: prev.itemId, itemName: prev.itemName } : null));
  };

  const groupFiltered = useMemo(() => {
    if (!hideZero) return groupRows;
    return groupRows.filter(r =>
      Math.abs(r.opening_qty) > 0.0005 || Math.abs(r.inward_qty) > 0.0005
      || Math.abs(r.outward_qty) > 0.0005 || Math.abs(r.closing_qty) > 0.0005,
    );
  }, [groupRows, hideZero]);
  const groupTotalPages = Math.max(1, Math.ceil(groupFiltered.length / GROUP_PAGE_SIZE));
  const groupSafePage = Math.min(groupPage, groupTotalPages);
  const groupPageRows = printing ? groupFiltered : groupFiltered.slice((groupSafePage - 1) * GROUP_PAGE_SIZE, groupSafePage * GROUP_PAGE_SIZE);
  const isGroupRoot = currentGroupId === null;
  const groupNoun = viewMode === 'categories' ? 'categories' : 'groups';

  const filtered = useMemo(() => {
    if (!hideZero) return rows;
    return rows.filter(r =>
      Math.abs(r.opening_qty)   > 0.0005
      || Math.abs(r.inward_qty)  > 0.0005
      || Math.abs(r.outward_qty) > 0.0005
      || Math.abs(r.closing_qty) > 0.0005,
    );
  }, [rows, hideZero]);

  // Batch summary hides on CLOSING QUANTITY only — a batch fully consumed
  // (0 remaining units) is "zero" even if its closing VALUE isn't exactly
  // zero (inward/outward rate differences leave a costing residual). Item/
  // group hideZero above stay on all 4 fields — this is batch-specific.
  const batchFiltered = useMemo(() => {
    if (!hideZero) return batchRows;
    return batchRows.filter(b => Math.abs(b.closing_qty) > 0.0005);
  }, [batchRows, hideZero]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    const dir = sortDir === 'asc' ? 1 : -1;
    arr.sort((a, b) => {
      const av = (a as any)[sortKey];
      const bv = (b as any)[sortKey];
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return String(av ?? '').localeCompare(String(bv ?? '')) * dir;
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const totalRows = sorted.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  const safePage = Math.min(page, totalPages);
  const startIdx = (safePage - 1) * pageSize;
  const endIdx = Math.min(startIdx + pageSize, totalRows);
  const pageRows = printing ? sorted : sorted.slice(startIdx, endIdx);

  const toggleSort = (k: SortKey) => {
    if (sortKey === k) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(k); setSortDir('asc'); }
  };
  const SortIcon = ({ col }: { col: SortKey }) => {
    if (sortKey !== col) return <ChevronsUpDown size={11} className="inline-block ml-1 text-slate-400" />;
    return sortDir === 'asc'
      ? <ChevronUp size={11} className="inline-block ml-1 text-blue-600" />
      : <ChevronDown size={11} className="inline-block ml-1 text-blue-600" />;
  };

  const cell = 'border border-slate-300 px-2.5 py-1.5 text-[13px] leading-snug';
  const cellNum = `${cell} text-right tabular-nums whitespace-nowrap`;
  const headCell = 'border border-slate-400 bg-slate-200 px-2 py-1.5 text-[12px] font-bold text-slate-700 uppercase tracking-wide sticky top-0 z-10';
  const headBtn = 'flex items-center select-none cursor-pointer hover:text-blue-700';

  return (
    <div className="flex flex-col w-full fixed left-0 right-0 top-14 bottom-16 sm:static sm:h-full sm:top-auto sm:bottom-auto" style={{ overscrollBehavior: "contain" }}>
      <div className="print-only mb-3 px-3" aria-hidden>
        <h1 className="text-xl font-bold mb-1">Stock Summary</h1>
        <div className="text-sm">From {displayDate(dateFrom)} to {displayDate(dateTo)}</div>
      </div>

      {/* ── Header bar ── */}
      <div className="flex-none bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between print:hidden">
        <div className="flex items-center gap-1 min-w-0 flex-1">
          <button
            onClick={() => {
              if (itemDrill) { backFromItemDrill(); return; }
              if (viewMode !== 'items' && !isGroupRoot) { goToBreadcrumb(breadcrumb.length - 2); return; }
              navigate(-1);
            }}
            className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100 -ml-1 flex-shrink-0" title="Back">
            <ChevronLeft size={20} />
          </button>
          {!itemDrill && (viewMode === 'items' || breadcrumb.length === 0) ? (
            <h1 className="text-[17px] font-bold text-slate-800 truncate">Stock Summary</h1>
          ) : (
            <div className="flex items-center gap-1 min-w-0 flex-wrap">
              <button onClick={() => { setItemDrill(null); goToBreadcrumb(-1); }} className="text-[14px] font-bold text-blue-600 hover:underline truncate">
                Stock Summary
              </button>
              {breadcrumb.map((b, i) => (
                <span key={b.id} className="flex items-center gap-1 min-w-0">
                  <DrillIcon size={13} className="text-slate-400 flex-shrink-0" />
                  <button onClick={() => { setItemDrill(null); goToBreadcrumb(i); }}
                    className={`text-[14px] font-semibold truncate max-w-[150px] ${!itemDrill && i === breadcrumb.length - 1 ? 'text-slate-800' : 'text-blue-600 hover:underline'}`}>
                    {b.name}
                  </button>
                </span>
              ))}
              {itemDrill && (
                <>
                  <span className="flex items-center gap-1 min-w-0">
                    <DrillIcon size={13} className="text-slate-400 flex-shrink-0" />
                    <button onClick={() => setItemDrill({ itemId: itemDrill.itemId, itemName: itemDrill.itemName })}
                      className={`text-[14px] font-semibold truncate max-w-[150px] ${!itemDrill.batchName ? 'text-slate-800' : 'text-blue-600 hover:underline'}`}>
                      {itemDrill.itemName}
                    </button>
                  </span>
                  {itemDrill.batchName && (
                    <span className="flex items-center gap-1 min-w-0">
                      <DrillIcon size={13} className="text-slate-400 flex-shrink-0" />
                      <span className="text-[14px] font-semibold text-slate-800 truncate max-w-[150px]">{itemDrill.batchName}</span>
                    </span>
                  )}
                </>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <div className="flex items-stretch border border-slate-300 rounded-lg overflow-hidden mr-1">
            <button onClick={() => setViewMode('items')} title="Flat item list"
              className={`p-2 transition-colors ${viewMode === 'items' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100 bg-white'}`}>
              <List size={16} />
            </button>
            <button onClick={() => { setViewMode('groups'); setBreadcrumb([]); }} title="By group (drill down)"
              className={`p-2 transition-colors border-l border-slate-300 ${viewMode === 'groups' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100 bg-white'}`}>
              <FolderTree size={16} />
            </button>
            <button onClick={() => { setViewMode('categories'); setBreadcrumb([]); }} title="By category (drill down)"
              className={`p-2 transition-colors border-l border-slate-300 ${viewMode === 'categories' ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100 bg-white'}`}>
              <Tag size={16} />
            </button>
          </div>
          <button onClick={() => setShowControls(v => !v)}
            className={`p-2 rounded-lg transition-colors ${showControls ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100'}`}
            title="Search & Filters (Alt+F2)">
            <Search size={18} />
          </button>
          <button onClick={handlePrint} disabled={(viewMode === 'items' ? rows.length : groupRows.length) === 0}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 disabled:opacity-40"
            title="Print">
            <Printer size={16} />
          </button>
          <button onClick={() => viewMode === 'items' ? load() : loadGroups()} className="p-2 rounded-lg text-slate-500 hover:bg-slate-100" title="Refresh">
            <RefreshCw size={16} className={(viewMode === 'items' ? loading : groupLoading) ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {/* ── Search & filter panel (slide-down) ── */}
      {showControls && (
        <div className="flex-none bg-slate-50 border-b border-slate-200 px-3 py-2.5 print:hidden">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[140px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Item / group…"
                autoFocus
                className="pl-9 pr-7 py-2 border border-slate-300 rounded-lg text-[13px] w-full outline-none focus:ring-2 focus:ring-blue-300 bg-white" />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  <X size={12} />
                </button>
              )}
            </div>
            <div className="flex items-stretch border border-slate-300 rounded-lg bg-white overflow-hidden">
              <div className="flex items-center gap-1 px-2 border-r border-slate-300 bg-slate-100">
                <Calendar size={12} className="text-slate-400" />
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">From</span>
              </div>
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
                className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1.5 w-[115px]" />
              <div className="flex items-center px-2 border-l border-r border-slate-300 bg-slate-100">
                <span className="text-[10px] text-slate-500 uppercase font-bold tracking-wide">To</span>
              </div>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
                className="text-[13px] text-slate-700 outline-none bg-transparent px-2 py-1.5 w-[115px]" />
            </div>
            <label className="flex items-center gap-1.5 px-3 py-2 border border-slate-300 rounded-lg bg-white text-[13px] text-slate-600 cursor-pointer whitespace-nowrap">
              <input type="checkbox" checked={hideZero} onChange={e => setHideZero(e.target.checked)} className="cursor-pointer" />
              Hide zero
            </label>
          </div>
        </div>
      )}


      {/* ── Mobile flat list (hidden on sm+) ── */}
      {viewMode === 'items' && !itemDrill && (
      <div className="sm:hidden flex-1 min-h-0 overflow-auto bg-white" style={{ overscrollBehavior: "contain" }}>
        {loading ? (
          <div className="text-center text-gray-400 py-8 text-[13px]">Loading…</div>
        ) : pageRows.length === 0 ? (
          <div className="text-center text-gray-400 py-8 text-[13px]">No items match the current filter</div>
        ) : (
          <>
            {pageRows.map((r) => (
              <div key={r.item_id} onClick={() => openItemLedger(r.item_id)} className="border-b border-gray-100 px-4 py-3 active:bg-gray-50">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className="font-semibold text-gray-900 text-[14px] leading-snug">{r.item_name}</div>
                      <button onClick={(e) => { e.stopPropagation(); openItemBatches(r.item_id, r.item_name); }}
                        className="p-1 -m-1 text-slate-400 hover:text-blue-600 flex-shrink-0" title="Batch-wise">
                        <Layers size={13} />
                      </button>
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5">{r.group_name || '—'}</div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <div className={`text-[14px] font-bold tabular-nums ${r.closing_value < 0 ? 'text-red-600' : 'text-blue-700'}`}>
                      ₹{fmt(r.closing_value)}
                    </div>
                    <div className="text-[11px] text-gray-400 tabular-nums mt-0.5">{fmtQty(r.closing_qty)} qty</div>
                  </div>
                </div>
                <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-400">
                  <span>Op: {fmtQty(r.opening_qty)}</span>
                  <span className="text-emerald-600">In: {fmtQty(r.inward_qty)}/{fmt(r.inward_value)}</span>
                  <span className="text-red-500">Out: {fmtQty(r.outward_qty)}/{fmt(r.outward_value)}</span>
                </div>
              </div>
            ))}
          </>
        )}
      </div>
      )}

      {/* ── Desktop table (hidden on mobile) ── */}
      {viewMode === 'items' && !itemDrill && (
      <div className="hidden sm:block overflow-auto max-h-[calc(100vh-210px)] bg-white border-x border-b border-slate-300 mx-3 mb-3 print:mx-0 print:overflow-visible print:max-h-none print:border-slate-400">
        <table className="border-collapse text-[13px] w-full">
          <thead>
            <tr>
              <th className={`${headCell} text-left`} rowSpan={2}>
                <div className={headBtn} onClick={() => toggleSort('item_name')}>Item<SortIcon col="item_name" /></div>
              </th>
              <th className={`${headCell} text-left w-32`} rowSpan={2}>
                <div className={headBtn} onClick={() => toggleSort('group_name')}>Group<SortIcon col="group_name" /></div>
              </th>
              <th className={`${headCell} text-center`} colSpan={2}>Opening</th>
              <th className={`${headCell} text-center`} colSpan={2}>Inward</th>
              <th className={`${headCell} text-center`} colSpan={2}>Outward</th>
              <th className={`${headCell} text-center`} colSpan={2}>Closing</th>
            </tr>
            <tr>
              <th className={`${headCell} text-right w-24`}>
                <div className={headBtn + ' justify-end'} onClick={() => toggleSort('opening_qty')}>Qty<SortIcon col="opening_qty" /></div>
              </th>
              <th className={`${headCell} text-right w-28`}>Value</th>
              <th className={`${headCell} text-right w-24`}>
                <div className={headBtn + ' justify-end'} onClick={() => toggleSort('inward_qty')}>Qty<SortIcon col="inward_qty" /></div>
              </th>
              <th className={`${headCell} text-right w-28`}>Value</th>
              <th className={`${headCell} text-right w-24`}>
                <div className={headBtn + ' justify-end'} onClick={() => toggleSort('outward_qty')}>Qty<SortIcon col="outward_qty" /></div>
              </th>
              <th className={`${headCell} text-right w-28`}>Value</th>
              <th className={`${headCell} text-right w-24`}>
                <div className={headBtn + ' justify-end'} onClick={() => toggleSort('closing_qty')}>Qty<SortIcon col="closing_qty" /></div>
              </th>
              <th className={`${headCell} text-right w-28`}>
                <div className={headBtn + ' justify-end'} onClick={() => toggleSort('closing_value')}>Value<SortIcon col="closing_value" /></div>
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className={`${cell} text-center text-slate-400 py-6`}>Loading…</td></tr>
            ) : pageRows.length === 0 ? (
              <tr><td colSpan={10} className={`${cell} text-center text-slate-400 py-6`}>No items match the current filter</td></tr>
            ) : (
              pageRows.map((r, i) => {
                const rowIdx = printing ? i : startIdx + i;
                const zebra = rowIdx % 2 === 1 ? 'bg-slate-50' : 'bg-white';
                const negativeStock = r.closing_qty < -0.0005;
                return (
                  <tr key={r.item_id} onClick={() => openItemLedger(r.item_id)} className={`${zebra} hover:bg-blue-50 cursor-pointer`}>
                    <td className={`${cell} font-medium text-slate-800`}>
                      <div className="flex items-center gap-1.5">
                        <span>{r.item_name}</span>
                        <button onClick={(e) => { e.stopPropagation(); openItemBatches(r.item_id, r.item_name); }}
                          className="p-0.5 text-slate-400 hover:text-blue-600 flex-shrink-0" title="Batch-wise">
                          <Layers size={13} />
                        </button>
                      </div>
                    </td>
                    <td className={`${cell} text-slate-600 text-[12px]`}>{r.group_name || '—'}</td>
                    <td className={cellNum + ' text-slate-700'}>{r.opening_qty !== 0 ? fmtQty(r.opening_qty) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-slate-600'}>{r.opening_value !== 0 ? fmt(r.opening_value) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-emerald-700'}>{r.inward_qty > 0 ? fmtQty(r.inward_qty) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-emerald-600'}>{r.inward_value > 0 ? fmt(r.inward_value) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-red-700'}>{r.outward_qty > 0 ? fmtQty(r.outward_qty) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-red-600'}>{r.outward_value > 0 ? fmt(r.outward_value) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' font-semibold ' + (negativeStock ? 'text-red-700' : 'text-blue-700')}>
                      {r.closing_qty !== 0 ? fmtQty(r.closing_qty) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className={cellNum + ' font-semibold ' + (negativeStock ? 'text-red-700' : 'text-blue-700')}>
                      {r.closing_value !== 0 ? fmt(r.closing_value) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
          {totalRows > 0 && (
            <tfoot>
              <tr className="bg-slate-200 font-bold sticky bottom-0">
                <td colSpan={3} className={`${cell} text-slate-700`}>Total ({totalRows} items)</td>
                <td className={cellNum + ' text-slate-700'}>{fmt(totals.opening_value)}</td>
                <td className={cell} />
                <td className={cellNum + ' text-emerald-700'}>{fmt(totals.inward_value)}</td>
                <td className={cell} />
                <td className={cellNum + ' text-red-700'}>{fmt(totals.outward_value)}</td>
                <td className={cell} />
                <td className={cellNum + ' text-blue-700'}>{fmt(totals.closing_value)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      )}

      {/* ── Group mode: mobile card list ── */}
      {viewMode !== 'items' && !itemDrill && (
      <div className="sm:hidden flex-1 min-h-0 overflow-auto bg-white" style={{ overscrollBehavior: 'contain' }}>
        {groupLoading ? (
          <div className="text-center text-gray-400 py-8 text-[13px]">Loading…</div>
        ) : groupPageRows.length === 0 ? (
          <div className="text-center text-gray-400 py-8 text-[13px]">No {isGroupRoot ? groupNoun : 'items'} match the current filter</div>
        ) : groupPageRows.map((r) => {
          const isGrp = r.row_type === 'group';
          return (
            <div key={`${r.row_type}-${r.id}`} onClick={() => drillDown(r)} className="border-b border-gray-100 px-4 py-3 active:bg-gray-50">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  {isGrp
                    ? <DrillIcon size={14} className="text-blue-400 flex-shrink-0" />
                    : <span className="w-3 h-3 rounded-full border border-slate-300 flex-shrink-0" />}
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      <div className={`text-[14px] leading-snug truncate ${isGrp ? 'font-bold text-gray-900' : 'font-semibold text-gray-800'}`}>{r.name}</div>
                      {!isGrp && (
                        <button onClick={(e) => { e.stopPropagation(); openItemBatches(r.id, r.name); }}
                          className="p-1 -m-1 text-slate-400 hover:text-blue-600 flex-shrink-0" title="Batch-wise">
                          <Layers size={13} />
                        </button>
                      )}
                    </div>
                    {isGrp && <div className="text-[11px] text-gray-400 mt-0.5">{r.item_count ?? 0} item(s)</div>}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className={`text-[14px] font-bold tabular-nums ${r.closing_value < 0 ? 'text-red-600' : 'text-blue-700'}`}>₹{fmt(r.closing_value)}</div>
                  <div className="text-[11px] text-gray-400 tabular-nums mt-0.5">{fmtQty(r.closing_qty)} qty</div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-400">
                <span>Op: {fmtQty(r.opening_qty)}</span>
                <span className="text-emerald-600">In: {fmtQty(r.inward_qty)}/{fmt(r.inward_value)}</span>
                <span className="text-red-500">Out: {fmtQty(r.outward_qty)}/{fmt(r.outward_value)}</span>
              </div>
            </div>
          );
        })}
      </div>
      )}

      {/* ── Group mode: desktop table ── */}
      {viewMode !== 'items' && !itemDrill && (
      <div className="hidden sm:block overflow-auto max-h-[calc(100vh-210px)] bg-white border-x border-b border-slate-300 mx-3 mb-3 print:mx-0 print:overflow-visible print:max-h-none print:border-slate-400">
        <table className="border-collapse text-[13px] w-full">
          <thead>
            <tr>
              <th className={`${headCell} text-left`} rowSpan={2}>{isGroupRoot ? (viewMode === 'categories' ? 'Category' : 'Group') : 'Particulars'}</th>
              <th className={`${headCell} text-center`} colSpan={2}>Opening</th>
              <th className={`${headCell} text-center`} colSpan={2}>Inward</th>
              <th className={`${headCell} text-center`} colSpan={2}>Outward</th>
              <th className={`${headCell} text-center`} colSpan={2}>Closing</th>
            </tr>
            <tr>
              <th className={`${headCell} text-right w-24`}>Qty</th>
              <th className={`${headCell} text-right w-28`}>Value</th>
              <th className={`${headCell} text-right w-24`}>Qty</th>
              <th className={`${headCell} text-right w-28`}>Value</th>
              <th className={`${headCell} text-right w-24`}>Qty</th>
              <th className={`${headCell} text-right w-28`}>Value</th>
              <th className={`${headCell} text-right w-24`}>Qty</th>
              <th className={`${headCell} text-right w-28`}>Value</th>
            </tr>
          </thead>
          <tbody>
            {groupLoading ? (
              <tr><td colSpan={9} className={`${cell} text-center text-slate-400 py-6`}>Loading…</td></tr>
            ) : groupPageRows.length === 0 ? (
              <tr><td colSpan={9} className={`${cell} text-center text-slate-400 py-6`}>No {isGroupRoot ? groupNoun : 'items'} match the current filter</td></tr>
            ) : groupPageRows.map((r, i) => {
              const zebra = i % 2 === 1 ? 'bg-slate-50' : 'bg-white';
              const isGrp = r.row_type === 'group';
              const negativeStock = r.closing_qty < -0.0005;
              return (
                <tr key={`${r.row_type}-${r.id}`} onClick={() => drillDown(r)} className={`${zebra} hover:bg-blue-50 ${isGrp ? 'cursor-pointer' : ''}`}>
                  <td className={`${cell} ${isGrp ? 'font-semibold text-slate-800' : 'text-slate-700'}`}>
                    <div className="flex items-center gap-2">
                      {isGrp
                        ? <DrillIcon size={14} className="text-blue-400 flex-shrink-0" />
                        : <span className="w-2 h-2 rounded-full border border-slate-300 flex-shrink-0" />}
                      <span>{r.name}</span>
                      {isGrp && <span className="text-[11px] text-slate-400">({r.item_count ?? 0})</span>}
                      {!isGrp && (
                        <button onClick={(e) => { e.stopPropagation(); openItemBatches(r.id, r.name); }}
                          className="p-0.5 text-slate-400 hover:text-blue-600 flex-shrink-0" title="Batch-wise">
                          <Layers size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className={cellNum + ' text-slate-700'}>{r.opening_qty !== 0 ? fmtQty(r.opening_qty) : <span className="text-slate-300">—</span>}</td>
                  <td className={cellNum + ' text-slate-600'}>{r.opening_value !== 0 ? fmt(r.opening_value) : <span className="text-slate-300">—</span>}</td>
                  <td className={cellNum + ' text-emerald-700'}>{r.inward_qty > 0 ? fmtQty(r.inward_qty) : <span className="text-slate-300">—</span>}</td>
                  <td className={cellNum + ' text-emerald-600'}>{r.inward_value > 0 ? fmt(r.inward_value) : <span className="text-slate-300">—</span>}</td>
                  <td className={cellNum + ' text-red-700'}>{r.outward_qty > 0 ? fmtQty(r.outward_qty) : <span className="text-slate-300">—</span>}</td>
                  <td className={cellNum + ' text-red-600'}>{r.outward_value > 0 ? fmt(r.outward_value) : <span className="text-slate-300">—</span>}</td>
                  <td className={cellNum + ' font-semibold ' + (negativeStock ? 'text-red-700' : 'text-blue-700')}>
                    {r.closing_qty !== 0 ? fmtQty(r.closing_qty) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className={cellNum + ' font-semibold ' + (negativeStock ? 'text-red-700' : 'text-blue-700')}>
                    {r.closing_value !== 0 ? fmt(r.closing_value) : <span className="text-slate-300">—</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
          {groupFiltered.length > 0 && (
            <tfoot>
              <tr className="bg-slate-200 font-bold sticky bottom-0">
                <td className={`${cell} text-slate-700`}>
                  {isGroupRoot ? `Total (${groupFiltered.length} ${groupNoun})` : `Total (${groupFiltered.filter(r => r.row_type === 'group').length} sub-${groupNoun}, ${groupFiltered.filter(r => r.row_type === 'item').length} items)`}
                </td>
                <td className={cell} />
                <td className={cellNum + ' text-slate-700'}>{fmt(groupTotals.opening_value)}</td>
                <td className={cell} />
                <td className={cellNum + ' text-emerald-700'}>{fmt(groupTotals.inward_value)}</td>
                <td className={cell} />
                <td className={cellNum + ' text-red-700'}>{fmt(groupTotals.outward_value)}</td>
                <td className={cell} />
                <td className={cellNum + ' text-blue-700'}>{fmt(groupTotals.closing_value)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      )}

      {/* Mobile — Grand Total fixed above pagination */}
      {viewMode === 'items' && !itemDrill && !printing && totalRows > 0 && (
        <div className="sm:hidden flex-none border-t border-gray-200 print:hidden">
          <div className="bg-blue-700 text-white px-4 py-2.5 flex justify-between items-center">
            <span className="font-bold text-sm tracking-widest">GRAND TOTAL</span>
            <span className="text-sm tabular-nums font-semibold">{totalRows} items</span>
          </div>
          <div className="flex divide-x divide-gray-200 bg-white">
            <div className="flex-1 px-3 py-2.5">
              <div className="text-[10px] uppercase text-gray-400 font-bold tracking-wide">Opening</div>
              <div className="text-[13px] font-bold text-slate-700 tabular-nums mt-0.5">₹{fmt(totals.opening_value)}</div>
            </div>
            <div className="flex-1 px-3 py-2.5">
              <div className="text-[10px] uppercase text-gray-400 font-bold tracking-wide">Inward</div>
              <div className="text-[13px] font-bold text-emerald-700 tabular-nums mt-0.5">₹{fmt(totals.inward_value)}</div>
            </div>
            <div className="flex-1 px-3 py-2.5">
              <div className="text-[10px] uppercase text-gray-400 font-bold tracking-wide">Outward</div>
              <div className="text-[13px] font-bold text-red-600 tabular-nums mt-0.5">₹{fmt(totals.outward_value)}</div>
            </div>
            <div className="flex-1 px-3 py-2.5">
              <div className="text-[10px] uppercase text-gray-400 font-bold tracking-wide">Closing</div>
              <div className="text-[13px] font-bold text-blue-700 tabular-nums mt-0.5">₹{fmt(totals.closing_value)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Group mode — Grand Total fixed above pagination (mobile) */}
      {viewMode !== 'items' && !itemDrill && !printing && groupFiltered.length > 0 && (
        <div className="sm:hidden flex-none border-t border-gray-200 print:hidden">
          <div className="bg-blue-700 text-white px-4 py-2.5 flex justify-between items-center">
            <span className="font-bold text-sm tracking-widest">GRAND TOTAL</span>
            <span className="text-sm tabular-nums font-semibold">
              {isGroupRoot ? `${groupFiltered.length} ${groupNoun}` : `${groupFiltered.length} rows`}
            </span>
          </div>
          <div className="flex divide-x divide-gray-200 bg-white">
            <div className="flex-1 px-3 py-2.5">
              <div className="text-[10px] uppercase text-gray-400 font-bold tracking-wide">Opening</div>
              <div className="text-[13px] font-bold text-slate-700 tabular-nums mt-0.5">₹{fmt(groupTotals.opening_value)}</div>
            </div>
            <div className="flex-1 px-3 py-2.5">
              <div className="text-[10px] uppercase text-gray-400 font-bold tracking-wide">Inward</div>
              <div className="text-[13px] font-bold text-emerald-700 tabular-nums mt-0.5">₹{fmt(groupTotals.inward_value)}</div>
            </div>
            <div className="flex-1 px-3 py-2.5">
              <div className="text-[10px] uppercase text-gray-400 font-bold tracking-wide">Outward</div>
              <div className="text-[13px] font-bold text-red-600 tabular-nums mt-0.5">₹{fmt(groupTotals.outward_value)}</div>
            </div>
            <div className="flex-1 px-3 py-2.5">
              <div className="text-[10px] uppercase text-gray-400 font-bold tracking-wide">Closing</div>
              <div className="text-[13px] font-bold text-blue-700 tabular-nums mt-0.5">₹{fmt(groupTotals.closing_value)}</div>
            </div>
          </div>
        </div>
      )}

      {/* Pagination — flex-none keeps it at bottom */}
      {viewMode === 'items' && !itemDrill && !printing && totalRows > pageSize && (
        <div className="flex-none flex items-center justify-between gap-2 px-3 py-1.5 border-t border-slate-200 bg-slate-50 text-[13px] text-slate-700 print:hidden">
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline">Rows per page:</span>
            <select value={autoFit ? 'auto' : pageSize}
              onChange={e => {
                if (e.target.value === 'auto') { setAutoFit(true); }
                else { setAutoFit(false); setPageSize(Number(e.target.value)); }
              }}
              className="border border-slate-300 rounded px-1.5 py-0.5 bg-white">
              <option value="auto">Auto ({pageSize})</option>
              {PAGE_SIZES.map(n => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <div className="tabular-nums">{`${startIdx + 1}–${endIdx} of ${totalRows}`}</div>
          <div className="flex items-center gap-1">
            <button onClick={() => setPage(1)} disabled={safePage === 1} className="p-1 border border-slate-300 rounded bg-white hover:bg-slate-50 disabled:opacity-40"><ChevronsLeft size={14} /></button>
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} className="p-1 border border-slate-300 rounded bg-white hover:bg-slate-50 disabled:opacity-40"><ChevronLeft size={14} /></button>
            <span className="px-2 tabular-nums">Page {safePage} / {totalPages}</span>
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} className="p-1 border border-slate-300 rounded bg-white hover:bg-slate-50 disabled:opacity-40"><ChevronRight size={14} /></button>
            <button onClick={() => setPage(totalPages)} disabled={safePage === totalPages} className="p-1 border border-slate-300 rounded bg-white hover:bg-slate-50 disabled:opacity-40"><ChevronsRight size={14} /></button>
          </div>
        </div>
      )}

      {/* Group mode — pagination */}
      {viewMode !== 'items' && !itemDrill && !printing && groupFiltered.length > GROUP_PAGE_SIZE && (
        <div className="flex-none flex items-center justify-between gap-2 px-3 py-1.5 border-t border-slate-200 bg-slate-50 text-[13px] text-slate-700 print:hidden">
          <div className="tabular-nums">{`${(groupSafePage - 1) * GROUP_PAGE_SIZE + 1}–${Math.min(groupSafePage * GROUP_PAGE_SIZE, groupFiltered.length)} of ${groupFiltered.length}`}</div>
          <div className="flex items-center gap-1">
            <button onClick={() => setGroupPage(1)} disabled={groupSafePage === 1} className="p-1 border border-slate-300 rounded bg-white hover:bg-slate-50 disabled:opacity-40"><ChevronsLeft size={14} /></button>
            <button onClick={() => setGroupPage(p => Math.max(1, p - 1))} disabled={groupSafePage === 1} className="p-1 border border-slate-300 rounded bg-white hover:bg-slate-50 disabled:opacity-40"><ChevronLeft size={14} /></button>
            <span className="px-2 tabular-nums">Page {groupSafePage} / {groupTotalPages}</span>
            <button onClick={() => setGroupPage(p => Math.min(groupTotalPages, p + 1))} disabled={groupSafePage === groupTotalPages} className="p-1 border border-slate-300 rounded bg-white hover:bg-slate-50 disabled:opacity-40"><ChevronRight size={14} /></button>
            <button onClick={() => setGroupPage(groupTotalPages)} disabled={groupSafePage === groupTotalPages} className="p-1 border border-slate-300 rounded bg-white hover:bg-slate-50 disabled:opacity-40"><ChevronsRight size={14} /></button>
          </div>
        </div>
      )}

      {/* ── Item drill level 1: batch list (mobile) ── */}
      {itemDrill && !itemDrill.batchName && (
        <div className="sm:hidden flex-1 min-h-0 overflow-auto bg-white" style={{ overscrollBehavior: 'contain' }}>
          {batchLoading ? (
            <div className="text-center text-gray-400 py-8 text-[13px]">Loading…</div>
          ) : batchFiltered.length === 0 ? (
            <div className="text-center text-gray-400 py-8 text-[13px]">No batch-tracked movement for this item in the selected date range</div>
          ) : batchFiltered.map(b => (
            <div key={b.batch_name} onClick={() => openBatchTransactions(b.batch_name)} className="border-b border-gray-100 px-4 py-3 active:bg-gray-50">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0 flex items-center gap-2">
                  <Layers size={13} className="text-blue-400 flex-shrink-0" />
                  <div className="font-semibold text-gray-800 text-[14px] leading-snug truncate">{b.batch_name}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className={`text-[14px] font-bold tabular-nums ${b.closing_value < 0 ? 'text-red-600' : 'text-blue-700'}`}>₹{fmt(b.closing_value)}</div>
                  <div className="text-[11px] text-gray-400 tabular-nums mt-0.5">{fmtQty(b.closing_qty)} qty</div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-400">
                <span>Op: {fmtQty(b.opening_qty)}</span>
                <span className="text-emerald-600">In: {fmtQty(b.inward_qty)}/{fmt(b.inward_value)}</span>
                <span className="text-red-500">Out: {fmtQty(b.outward_qty)}/{fmt(b.outward_value)}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Item drill level 1: batch list (desktop) ── */}
      {itemDrill && !itemDrill.batchName && (
        <div className="hidden sm:block overflow-auto max-h-[calc(100vh-210px)] bg-white border-x border-b border-slate-300 mx-3 mb-3 print:mx-0 print:overflow-visible print:max-h-none print:border-slate-400">
          <table className="border-collapse text-[13px] w-full">
            <thead>
              <tr>
                <th className={`${headCell} text-left`} rowSpan={2}>Batch</th>
                <th className={`${headCell} text-center`} colSpan={2}>Opening</th>
                <th className={`${headCell} text-center`} colSpan={2}>Inward</th>
                <th className={`${headCell} text-center`} colSpan={2}>Outward</th>
                <th className={`${headCell} text-center`} colSpan={2}>Closing</th>
              </tr>
              <tr>
                <th className={`${headCell} text-right w-24`}>Qty</th>
                <th className={`${headCell} text-right w-28`}>Value</th>
                <th className={`${headCell} text-right w-24`}>Qty</th>
                <th className={`${headCell} text-right w-28`}>Value</th>
                <th className={`${headCell} text-right w-24`}>Qty</th>
                <th className={`${headCell} text-right w-28`}>Value</th>
                <th className={`${headCell} text-right w-24`}>Qty</th>
                <th className={`${headCell} text-right w-28`}>Value</th>
              </tr>
            </thead>
            <tbody>
              {batchLoading ? (
                <tr><td colSpan={9} className={`${cell} text-center text-slate-400 py-6`}>Loading…</td></tr>
              ) : batchFiltered.length === 0 ? (
                <tr><td colSpan={9} className={`${cell} text-center text-slate-400 py-6`}>No batch-tracked movement for this item in the selected date range</td></tr>
              ) : batchFiltered.map((b, i) => {
                const zebra = i % 2 === 1 ? 'bg-slate-50' : 'bg-white';
                const negativeStock = b.closing_qty < -0.0005;
                return (
                  <tr key={b.batch_name} onClick={() => openBatchTransactions(b.batch_name)} className={`${zebra} hover:bg-blue-50 cursor-pointer`}>
                    <td className={`${cell} font-medium text-slate-800`}>
                      <div className="flex items-center gap-2">
                        <DrillIcon size={13} className="text-blue-400 flex-shrink-0" />
                        <span>{b.batch_name}</span>
                      </div>
                    </td>
                    <td className={cellNum + ' text-slate-700'}>{b.opening_qty !== 0 ? fmtQty(b.opening_qty) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-slate-600'}>{b.opening_value !== 0 ? fmt(b.opening_value) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-emerald-700'}>{b.inward_qty > 0 ? fmtQty(b.inward_qty) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-emerald-600'}>{b.inward_value > 0 ? fmt(b.inward_value) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-red-700'}>{b.outward_qty > 0 ? fmtQty(b.outward_qty) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-red-600'}>{b.outward_value > 0 ? fmt(b.outward_value) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' font-semibold ' + (negativeStock ? 'text-red-700' : 'text-blue-700')}>
                      {b.closing_qty !== 0 ? fmtQty(b.closing_qty) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className={cellNum + ' font-semibold ' + (negativeStock ? 'text-red-700' : 'text-blue-700')}>
                      {b.closing_value !== 0 ? fmt(b.closing_value) : <span className="text-slate-300">—</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            {batchFiltered.length > 0 && (
              <tfoot>
                <tr className="bg-slate-200 font-bold sticky bottom-0">
                  <td className={`${cell} text-slate-700`}>Total ({batchFiltered.length} batch{batchFiltered.length === 1 ? '' : 'es'})</td>
                  <td className={cell} />
                  <td className={cellNum + ' text-slate-700'}>{fmt(batchTotals.opening_value)}</td>
                  <td className={cell} />
                  <td className={cellNum + ' text-emerald-700'}>{fmt(batchTotals.inward_value)}</td>
                  <td className={cell} />
                  <td className={cellNum + ' text-red-700'}>{fmt(batchTotals.outward_value)}</td>
                  <td className={cell} />
                  <td className={cellNum + ' text-blue-700'}>{fmt(batchTotals.closing_value)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}

      {/* ── Item drill level 2: one batch's voucher register (mobile) ── */}
      {itemDrill?.batchName && (
        <div className="sm:hidden flex-1 min-h-0 overflow-auto bg-white" style={{ overscrollBehavior: 'contain' }}>
          {txnLoading ? (
            <div className="text-center text-gray-400 py-8 text-[13px]">Loading…</div>
          ) : txnRows.length === 0 ? (
            <div className="text-center text-gray-400 py-8 text-[13px]">No vouchers for this batch in the selected date range</div>
          ) : txnRows.map(r => (
            <div key={`${r.vch_id}-${r.sno}`} className="border-b border-gray-100 px-4 py-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 text-[14px] leading-snug truncate">{r.particulars}</div>
                  <div className="text-[11px] text-gray-400 mt-0.5">{r.vch_type_name || '—'} · {r.vch_no || '—'} · {displayDate(r.vch_date)}</div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className={`text-[14px] font-bold tabular-nums ${r.closing_value < 0 ? 'text-red-600' : 'text-blue-700'}`}>₹{fmt(r.closing_value)}</div>
                  <div className="text-[11px] text-gray-400 tabular-nums mt-0.5">{fmtQty(r.closing_qty)} qty</div>
                </div>
              </div>
              <div className="flex items-center gap-3 mt-1.5 text-[11px] text-gray-400">
                <span>Op: {fmtQty(r.opening_qty)}</span>
                {r.inward_qty !== 0 && <span className="text-emerald-600">In: {fmtQty(r.inward_qty)}/{fmt(r.inward_value)}</span>}
                {r.outward_qty !== 0 && <span className="text-red-500">Out: {fmtQty(r.outward_qty)}/{fmt(r.outward_value)}</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Item drill level 2: one batch's voucher register (desktop) ── */}
      {itemDrill?.batchName && (
        <div className="hidden sm:block overflow-auto max-h-[calc(100vh-210px)] bg-white border-x border-b border-slate-300 mx-3 mb-3 print:mx-0 print:overflow-visible print:max-h-none print:border-slate-400">
          <table className="border-collapse text-[13px] w-full">
            <thead>
              <tr>
                <th className={`${headCell} text-right w-12`} rowSpan={2}>S.No</th>
                <th className={`${headCell} text-left`} rowSpan={2}>Particulars</th>
                <th className={`${headCell} text-left w-28`} rowSpan={2}>Vch Type</th>
                <th className={`${headCell} text-left w-24`} rowSpan={2}>Vch No.</th>
                <th className={`${headCell} text-left w-28`} rowSpan={2}>Vch Date</th>
                <th className={`${headCell} text-center`} colSpan={2}>Opening</th>
                <th className={`${headCell} text-center`} colSpan={2}>Inward</th>
                <th className={`${headCell} text-center`} colSpan={2}>Outward</th>
                <th className={`${headCell} text-center`} colSpan={2}>Closing</th>
              </tr>
              <tr>
                <th className={`${headCell} text-right w-20`}>Qty</th>
                <th className={`${headCell} text-right w-24`}>Value</th>
                <th className={`${headCell} text-right w-20`}>Qty</th>
                <th className={`${headCell} text-right w-24`}>Value</th>
                <th className={`${headCell} text-right w-20`}>Qty</th>
                <th className={`${headCell} text-right w-24`}>Value</th>
                <th className={`${headCell} text-right w-20`}>Qty</th>
                <th className={`${headCell} text-right w-24`}>Value</th>
              </tr>
            </thead>
            <tbody>
              {txnLoading ? (
                <tr><td colSpan={13} className={`${cell} text-center text-slate-400 py-6`}>Loading…</td></tr>
              ) : txnRows.length === 0 ? (
                <tr><td colSpan={13} className={`${cell} text-center text-slate-400 py-6`}>No vouchers for this batch in the selected date range</td></tr>
              ) : txnRows.map((r, i) => {
                const zebra = i % 2 === 1 ? 'bg-slate-50' : 'bg-white';
                const negativeStock = r.closing_qty < -0.0005;
                return (
                  <tr key={`${r.vch_id}-${r.sno}`} className={`${zebra} hover:bg-blue-50`}>
                    <td className={`${cell} text-right text-slate-500`}>{r.sno}</td>
                    <td className={`${cell} font-medium text-slate-800`}>{r.particulars}</td>
                    <td className={`${cell} text-slate-600 text-[12px]`}>{r.vch_type_name || '—'}</td>
                    <td className={`${cell} text-slate-600`}>
                      <button onClick={() => navigate(`/billing/vouchers/edit/${r.vch_id}`)}
                        className="text-blue-600 hover:text-blue-800 hover:underline font-medium">{r.vch_no || '—'}</button>
                    </td>
                    <td className={`${cell} text-slate-600`}>{displayDate(r.vch_date)}</td>
                    <td className={cellNum + ' text-slate-700'}>{r.opening_qty !== 0 ? fmtQty(r.opening_qty) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-slate-600'}>{r.opening_value !== 0 ? fmt(r.opening_value) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-emerald-700'}>{r.inward_qty !== 0 ? fmtQty(r.inward_qty) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-emerald-600'}>{r.inward_value !== 0 ? fmt(r.inward_value) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-red-700'}>{r.outward_qty !== 0 ? fmtQty(r.outward_qty) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' text-red-600'}>{r.outward_value !== 0 ? fmt(r.outward_value) : <span className="text-slate-300">—</span>}</td>
                    <td className={cellNum + ' font-semibold ' + (negativeStock ? 'text-red-700' : 'text-blue-700')}>{fmtQty(r.closing_qty)}</td>
                    <td className={cellNum + ' font-semibold ' + (negativeStock ? 'text-red-700' : 'text-blue-700')}>{fmt(r.closing_value)}</td>
                  </tr>
                );
              })}
            </tbody>
            {txnRows.length > 0 && (
              <tfoot>
                <tr className="bg-slate-200 font-bold sticky bottom-0">
                  <td colSpan={5} className={`${cell} text-slate-700`}>Opening / Closing ({txnRows.length} vouchers)</td>
                  <td className={cellNum + ' text-slate-700'}>{fmtQty(txnOpening.qty)}</td>
                  <td className={cellNum + ' text-slate-700'}>{fmt(txnOpening.value)}</td>
                  <td className={cell} />
                  <td className={cell} />
                  <td className={cell} />
                  <td className={cell} />
                  <td className={cellNum + ' text-blue-700'}>{fmtQty(txnClosing.qty)}</td>
                  <td className={cellNum + ' text-blue-700'}>{fmt(txnClosing.value)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, color, bold }: { label: string; value: number; color: string; bold?: boolean }) {
  return (
    <div className="flex items-center gap-2 px-3 py-2 border-r border-slate-300">
      <span className="text-slate-500 uppercase text-[11px] font-bold tracking-wide">{label}</span>
      <span className={`tabular-nums ${bold ? 'font-bold' : 'font-semibold'} ${color}`}>{fmt(value)}</span>
    </div>
  );
}
